<script setup lang="ts">
import { computed, ref, watch, onMounted, onUnmounted } from 'vue'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import { useRoute } from 'vue-router'
import { debounce } from 'lodash-es'

import { useAuthStore } from '@/stores/auth.store'
import { useIdeStore } from '@/stores/ide.store'
import { useProjectManager } from '@/stores/projectManager.store'
import {
  LiveCodeActionsType,
  useLiveCodeStore,
  type ILiveCodeActions
} from '@/stores/liveCode.store'

import { IDEVIEWMODELS } from '@/utils/models'
// import ideModal from '@/components/ide/ide/IdeModal.vue'
import projectsService, { GENERIC_TYPE } from '@/services/ide/projects.service'
import utilModelsService from '@/services/util.models.service'
import {
  type IInviteCollaborationUsers,
  type IHttpStatus,
  AccessType,
  AccessPermission
} from '@/services/ide/projects.service'
import liveCodingService from '@/services/ide/liveCoding.service'
import Button from '@/components/atoms/button/index.vue'

import Globe from '@/assets/images/svg/globe.svg?component'

export interface IManageCollaborator {
  firstName: String
  lastName: String
  collaboratorEmail: String
  readOnly: Boolean
  permissionDropdown: Boolean
  profilePictureUrl?: String
  sharedInGeneralAccess: boolean
}

export interface IGenericLoading {
  type: string
  message: string
}

export interface IGeneralAccess {
  type: AccessType
  permission: AccessPermission
  isFetching: boolean
  awaitingURL: boolean
}

const authStore = useAuthStore()
const ideStore = useIdeStore()
const projectManagerStore = useProjectManager()
const liveCodeStore = useLiveCodeStore()
const route = useRoute()

const manageCollaboratorArray = ref<IManageCollaborator[]>([])
const isPermissionLoading = ref<boolean>(false)
const httpStatus = ref<IHttpStatus>({ status: '', message: '' })
const isGenericLoading = ref<IGenericLoading>({ type: '', message: '' })
const httpStatusTimeOut = ref<NodeJS.Timeout | number | null>(null)

const permissionTglRef = ref<HTMLElement | null>(null)
const permissionDdlRef = ref<HTMLElement | null>(null)

const generalAccess = ref<IGeneralAccess>({
  type: AccessType.RESTRICTED,
  permission: AccessPermission.READ_ONLY,
  isFetching: projectManagerStore.loadCollabModal,
  awaitingURL: true
})
const urlCopied = ref<boolean>(false)
const url = ref<string | null>(null)
const resizeObserver = ref<ResizeObserver | null>(null)

const projectDetails = computed(() => {
  return ideStore.isSelectedProject ? ideStore.isSelectedProject : ideStore.isProject
})

const projectOwner = computed(() => {
  if (ideStore.isProject?.ownerFirstName) {
    return {
      firstName: ideStore.isProject?.ownerFirstName ?? '',
      lastName: ideStore.isProject?.ownerLastName ?? '',
      profilePictureUrl: ideStore.isProject?.ownerProfilePictureUrl ?? ''
    }
  } else {
    return {
      firstName: authStore?.firstName ?? '',
      lastName: authStore?.lastName ?? '',
      profilePictureUrl: authStore?.profilePictureUrl ?? ''
    }
  }
})

const isFetching = computed(() => {
  return (
    (isGenericLoading.value?.type === GENERIC_TYPE.FETCH && !!isGenericLoading.value.message) ||
    projectManagerStore.loadCollabModal
  )
})

const isUsersEmpty = computed(() => {
  return manageCollaboratorArray.value?.length < 1 && !isGenericLoading.value?.message
})

/**
 * @param users - setting the value from viewer to Editor and vise versa
 * @param selectedValue - to avoid changing the status when we are clicking on the selected value again
 * @description - Changing the status from viewer to editor and vise versa
 */
const changePermissionStatus = async (users: IManageCollaborator, selectedValue: boolean) => {
  users.permissionDropdown = false
  if (selectedValue === users.readOnly) return
  else {
    users.readOnly = users.readOnly === true ? false : true
    await editCollaboratorStatus(users.readOnly as boolean, users.collaboratorEmail as string)
  }
}

/**
 * @description - makes an API call to get the list of collaborator
 */
const showCollaboratorList = async () => {
  isGenericLoading.value = { type: GENERIC_TYPE.FETCH, message: 'Loading...' }
  await projectsService
    .listOfCollaborator(projectDetails.value)
    .then(async (response) => {
      addDropDownValue(response.data?.projects)
      isGenericLoading.value = { type: '', message: '' }
      if (!projectManagerStore.comingFromProjectManager) {
        openLiveCodingCollabAdded()
        if (liveCodeStore.isLiveCodingActive) {
          broadcastAllCollab(response.data?.projects)
        }
      }
    })
    .catch(() => {
      isGenericLoading.value = { type: '', message: '' }
    })
}

/**
 * @description to broadcast collaborator
 * @param response - checking the collab options
 */
const broadcastAllCollab = (response: IManageCollaborator[]) => {
  response?.map(async (array: IManageCollaborator) => {
    if (array.readOnly) {
      const liveCodeAction: ILiveCodeActions = {
        type: LiveCodeActionsType.LIVE_WITH_VIEW,
        data: {
          collaboratorEmail: array.collaboratorEmail
        }
      }
      await liveCodingService.broudcastMessage(liveCodeAction)
    } else {
      const liveCodeAction: ILiveCodeActions = {
        type: LiveCodeActionsType.LIVE_WITH_EDIT,
        data: {
          collaboratorEmail: array.collaboratorEmail
        }
      }
      await liveCodingService.broudcastMessage(liveCodeAction)
    }
  })
}

/**
 * @param array - added the permission dropdown key in the array
 * @description - adding permission dropdown key in the array
 */
const addDropDownValue = (array: IManageCollaborator[]) => {
  manageCollaboratorArray.value = array
    .filter((collaborator: IManageCollaborator) => !collaborator.sharedInGeneralAccess)
    .map((collaborator: IManageCollaborator) => ({
      ...collaborator,
      permissionDropdown: false
    }))
}

/**
 * Delete project
 * @param email IProject
 */
const deleteManageCollaborator = async (email: string) => {
  const deleteCollaboratorReqObject: IInviteCollaborationUsers = {
    email: email,
    projectId: projectDetails.value?.id,
    language: projectDetails.value.language || route.meta.language
  }
  await utilModelsService
    .confirmPromise(
      'Are you sure, do you want to remove this collaborator?',
      null,
      IDEVIEWMODELS.PROJECTSHARE
    )
    .then(async () => {
      httpStatus.value = { status: '', message: '' }
      isGenericLoading.value = { type: GENERIC_TYPE.DELETE, message: 'Deleting collaborator...' }
      await projectsService
        .deleteCollaboratorInProject(deleteCollaboratorReqObject)
        .then(async (response) => {
          httpStatus.value = {
            status: GENERIC_TYPE.SUCCESS,
            message: 'Collaborator removed successfully'
          }
          isGenericLoading.value = { type: '', message: '' }
          manageCollaboratorArray.value = manageCollaboratorArray.value?.filter(
            (item) => item.collaboratorEmail !== response.data.collaboratorEmail
          )
          if (!checkEditorCollab(true) && liveCodeStore.isLiveCodingActive) {
            await liveCodingCollabActionDisconnect(response)
            liveCodingService.disconnectLiveCoding()
          }
        })
        .catch(() => {
          httpStatus.value = {
            status: 'error',
            message: 'Error removing your collaborator. Please try again later.'
          }
          isGenericLoading.value = { type: '', message: '' }
        })
    })
    .catch(() => {})
}

/**
 * @param permissionStatus - change the user status
 * @param email - selected user's email
 * @description - to add collaborator
 */
const editCollaboratorStatus = async (permissionStatus: boolean, email: string) => {
  const addCollaboratorReqObject: IInviteCollaborationUsers = {
    email: email,
    readOnly: permissionStatus,
    projectId: projectDetails.value?.id,
    language: projectDetails.value.language || route.meta.language,
    projectName: projectDetails.value.name
  }
  isPermissionLoading.value = true
  await projectsService
    .addCollaboratorInProject(addCollaboratorReqObject)
    .then(async (response) => {
      isPermissionLoading.value = false
      liveCodingCollabChangeAction(response)
    })
    .catch(() => (isPermissionLoading.value = false))
}

/**
 * @description - opening the live coding according to the option
 * @param liveCodingAction - connect live coding according to the boolean
 */
const openLiveCodingCollabAdded = (liveCodingAction: boolean = false) => {
  if (!liveCodeStore.isLiveCodingActive) {
    liveCodingService.initColab(liveCodingAction)
    liveCodeStore.setIsProjectLoaded(true)
  }
}

/**
 * @description - checking the readonly collab
 * @param  checkFromLocalArray - checking from the current array
 * @returns - returning if the collab is there or not
 */
const checkEditorCollab = (checkFromLocalArray: boolean = false) => {
  const collabArray = checkFromLocalArray
    ? manageCollaboratorArray.value
    : projectManagerStore?.collaboratorList ?? []

  if (collabArray.length != 0) return true
  return false
}

/**
 * @description to remove collaborator
 * @param response - checking the collab options
 */
const liveCodingCollabActionDisconnect = async (response: any) => {
  const liveCodeAction: ILiveCodeActions = {
    type: LiveCodeActionsType.REMOVE_COLLAB,
    data: {
      collaboratorEmail: response.data.collaboratorEmail,
      projectId: projectDetails.value?.id
    }
  }
  await liveCodingService.broudcastMessage(liveCodeAction)
}
/**
 * @description to change permission collaborator
 * @param response - checking the collab options
 */
const liveCodingCollabChangeAction = async (response: any) => {
  if (liveCodeStore.isLiveCodingActive) {
    if (response.data.readOnly) {
      const liveCodeAction: ILiveCodeActions = {
        type: LiveCodeActionsType.LIVE_WITH_VIEW,
        data: {
          collaboratorEmail: response.data.collaboratorEmail
        }
      }
      await liveCodingService.broudcastMessage(liveCodeAction)
    } else {
      const liveCodeAction: ILiveCodeActions = {
        type: LiveCodeActionsType.LIVE_WITH_EDIT,
        data: {
          collaboratorEmail: response.data.collaboratorEmail
        }
      }
      await liveCodingService.broudcastMessage(liveCodeAction)
    }
  }
}

/**
 * @param type Update access type of project for general access
 */
const handleUpdateType = (type: AccessType) => {
  generalAccess.value.type = type
  generalAccess.value.permission = AccessPermission.READ_ONLY
  projectsService
    .updateGeneralAccess({
      projectId: ideStore.project?.id,
      permission: generalAccess.value.permission,
      type: generalAccess.value.type
    })
    .then((response) => {
      url.value = response.data.link ?? null
      generalAccess.value.awaitingURL = false
      openLiveCodingCollabAdded(true)
      projectManagerStore?.collaboratorList?.map(async (res: IManageCollaborator) => {
        if (type === AccessType.RESTRICTED) {
          const liveCodeAction: ILiveCodeActions = {
            type: LiveCodeActionsType.REMOVE_COLLAB,
            data: {
              collaboratorEmail: res.collaboratorEmail,
              projectId: projectDetails.value?.id
            }
          }
          await liveCodingService.broudcastMessage(liveCodeAction)
        }
      })
    })
}

/**
 * @param permission Update permission of project for general access
 */
const handleUpdatePermission = (permission: AccessPermission) => {
  generalAccess.value.permission = permission
  projectsService
    .updateGeneralAccess({
      projectId: ideStore.project?.id,
      permission: generalAccess.value.permission,
      type: generalAccess.value.type
    })
    .then((response) => {
      url.value = response.data.link ?? null
      projectManagerStore?.collaboratorList?.map(async (res: IManageCollaborator) => {
        if (res?.sharedInGeneralAccess) {
          if (permission === AccessPermission.READ_ONLY) {
            const liveCodeAction: ILiveCodeActions = {
              type: LiveCodeActionsType.LIVE_WITH_VIEW,
              data: {
                collaboratorEmail: res.collaboratorEmail
              }
            }
            await liveCodingService.broudcastMessage(liveCodeAction)
          } else {
            const liveCodeAction: ILiveCodeActions = {
              type: LiveCodeActionsType.LIVE_WITH_EDIT,
              data: {
                collaboratorEmail: res.collaboratorEmail
              }
            }
            await liveCodingService.broudcastMessage(liveCodeAction)
          }
        }
      })
      generalAccess.value.awaitingURL = false
    })
}

/**
 *
 */
const initManageCollaborators = () => {
  /* Reset State */
  generalAccess.value.isFetching = false
  generalAccess.value.permission = AccessPermission.READ_ONLY
  generalAccess.value.type = AccessType.RESTRICTED

  if (ideStore.isSettingPopupHs === IDEVIEWMODELS.PROJECTSHARE) {
    showCollaboratorList()

    generalAccess.value.isFetching = true
    projectsService
      .getGeneralAccess(projectDetails.value?.id)
      .then((response) => {
        if (response.data) {
          generalAccess.value.type = response.data.type ?? AccessType.RESTRICTED
          generalAccess.value.permission = response.data.permission ?? AccessPermission.READ_ONLY
          url.value = response.data.link ?? null
          generalAccess.value.awaitingURL = false
        } else {
          generalAccess.value.awaitingURL = true
        }
      })
      .finally(() => {
        generalAccess.value.isFetching = false
      })
  }
}

/**
 * Copy general access URL
 */
const handleCopyURL = () => {
  if (url.value) window.navigator.clipboard.writeText(url.value)
  urlCopied.value = true
  setTimeout(() => {
    urlCopied.value = false
  }, 1000)
}

/**
 * Sync dropdown width with toggle
 */
const setDropdownWidth = debounce(() => {
  const toggle = permissionTglRef.value
  const dropdown = permissionDdlRef.value
  if (toggle && dropdown) {
    const toggleWidth = toggle.getBoundingClientRect().width
    dropdown.style.width = `${toggleWidth}px`
  }
}, 100)

onMounted(() => {
  initManageCollaborators()
  resizeObserver.value = new ResizeObserver(setDropdownWidth)
})

onUnmounted(() => {
  const toggle = permissionTglRef.value
  if (toggle) resizeObserver.value?.unobserve(toggle)
})

watch(permissionTglRef, (toggle) => {
  if (toggle) resizeObserver.value?.observe(toggle)
})

watch(
  () => ideStore.isSettingPopupHs,
  () => {
    if (!ideStore.isDefaultIde) {
      initManageCollaborators()
    }
  }
)

watch(
  () => projectDetails.value,
  () => {
    if (projectDetails.value && ideStore.isDefaultIde) {
      initManageCollaborators()
    }
  }
)

watch(httpStatus, () => {
  if (httpStatus.value.message) {
    if (httpStatusTimeOut.value) clearTimeout(httpStatusTimeOut.value)
    httpStatusTimeOut.value = setTimeout(() => {
      httpStatus.value = { status: '', message: '' }
    }, 3000)
  }
})
</script>

<template>
  <div class="flex h-full w-full flex-col justify-between">
    <div class="flex flex-col">
      <!-- User Access -->
      <div class="h-full">
        <h4 class="text-high mb-3 flex justify-between text-sm font-semibold">
          <p>Users with access</p>
          <!-- Notification bar 📲 -->
          <Transition
            enter-active-class="transition-opacity duration-300 ease-in-out"
            leave-active-class="transition-opacity duration-300 ease-in-out"
            enter-from-class="opacity-0"
            enter-to-class="opacity-100"
            leave-from-class="opacity-100"
            leave-to-class="opacity-0"
            mode="out-in"
          >
            <p v-if="isPermissionLoading">User permission updated</p>
            <p
              v-else-if="httpStatus?.status"
              :class="httpStatus.status === GENERIC_TYPE.SUCCESS ? 'success' : 'error'"
            >
              {{ httpStatus.message }}
            </p>
            <p v-else-if="isGenericLoading?.type === GENERIC_TYPE.DELETE">
              {{ isGenericLoading.message }}
            </p>
          </Transition>
        </h4>
        <div class="flex flex-col">
          <div class="-m-1.5 overflow-x-auto">
            <div class="inline-block min-w-full align-middle">
              <!-- Loading Screen 🛞 -->
              <div
                v-if="isFetching"
                class="flex h-full min-h-72 items-center justify-center space-x-2"
              >
                <FontAwesomeIcon spin icon="fa-spinner" class="h-4 w-4" />
                <span class="text-mid font-medium">Fetching user data, please wait....</span>
              </div>
              <div v-else class="h-72 overflow-scroll">
                <table class="min-w-full">
                  <tbody>
                    <tr v-if="projectOwner.firstName" class="hover:surface-primary-header">
                      <td class="text-high space-x-3 whitespace-nowrap p-2 text-sm font-medium">
                        <div class="flex items-center gap-x-3">
                          <div
                            class="background-primary stroke-primary-default flex h-9 w-9 items-center justify-center overflow-hidden rounded-full border"
                          >
                            <img
                              v-if="projectOwner.profilePictureUrl"
                              :src="projectOwner.profilePictureUrl.toString()"
                              alt="user profile pic"
                            />
                            <span v-else class="text-high text-sm font-semibold">
                              {{ projectOwner.firstName.toString().charAt(0).toUpperCase() }}
                            </span>
                          </div>
                          <span>{{ projectOwner.firstName }} {{ projectOwner.lastName }}</span>
                        </div>
                      </td>
                      <td class="whitespace-nowrap p-2 text-xs">
                        <div
                          class="color-secondary-600 flex w-max min-w-20 cursor-default items-center justify-center gap-1 rounded-full border border-new-secondary-600 px-2 py-1"
                        >
                          Owner
                        </div>
                      </td>
                      <td class="whitespace-nowrap p-2 text-end text-sm font-medium"></td>
                    </tr>
                    <tr v-if="isUsersEmpty">
                      <th colspan="3" class="p-2">
                        <div class="flex min-h-52 items-center justify-center">
                          <div class="flex flex-col items-center justify-center text-center">
                            <FontAwesomeIcon
                              icon="fa-search"
                              class="icon-ide-default mb-4 h-8 w-8"
                            />
                            <h2 class="text-low font-light">No Match Found</h2>
                          </div>
                        </div>
                      </th>
                    </tr>
                    <tr
                      v-else
                      v-for="(users, idx) in manageCollaboratorArray"
                      :key="idx"
                      class="hover:surface-primary-header"
                    >
                      <td class="text-high space-x-3 whitespace-nowrap p-2 text-sm font-medium">
                        <div class="flex items-center gap-x-3">
                          <div
                            class="background-primary stroke-primary-default flex h-9 w-9 items-center justify-center overflow-hidden rounded-full border"
                          >
                            <img
                              v-if="users?.profilePictureUrl"
                              :src="users.profilePictureUrl.toString()"
                              alt="user profile pic"
                            />
                            <span v-else class="text-high text-sm font-semibold">
                              {{ users.firstName.toString().charAt(0).toUpperCase() }}
                            </span>
                          </div>
                          <span>{{ users.firstName }} {{ users.lastName }}</span>
                        </div>
                      </td>
                      <td class="whitespace-nowrap p-2 text-xs">
                        <div
                          class="hs-dropdown relative inline-flex [--offset:4] [--placement:bottom-right]"
                        >
                          <div
                            class="color-secondary-600 flex w-full min-w-20 cursor-pointer items-center justify-center gap-1 rounded-full border border-new-secondary-600 px-2 py-1"
                          >
                            <p>{{ users.readOnly ? 'Viewer' : 'Editor' }}</p>
                            <FontAwesomeIcon icon="fa-angle-down" class="h-3 w-3" />
                          </div>
                          <div
                            class="hs-dropdown-menu duration surface-primary-base stroke-primary-default z-10 mt-2 hidden min-w-20 rounded-lg border opacity-0 shadow transition-[opacity,margin] before:absolute before:-top-1 before:start-0 before:h-1 before:w-full after:absolute after:-bottom-4 after:start-0 after:h-4 after:w-full hs-dropdown-open:opacity-100"
                            role="menu"
                            aria-orientation="vertical"
                            aria-labelledby="hs-dropdown-default"
                          >
                            <div class="space-y-0.5 p-1">
                              <span
                                class="hover:surface-primary-header flex cursor-pointer items-center gap-x-3.5 rounded-md px-3 py-1 text-sm focus:outline-none"
                                :class="
                                  users.readOnly === true &&
                                  'stroke-input-default border border-opacity-75'
                                "
                                @click="changePermissionStatus(users, true)"
                              >
                                Viewer
                              </span>
                              <span
                                class="hover:surface-primary-header flex cursor-pointer items-center gap-x-3.5 rounded-md px-3 py-1 text-sm focus:outline-none"
                                :class="
                                  users.readOnly === false &&
                                  'stroke-input-default border border-opacity-75'
                                "
                                @click="changePermissionStatus(users, false)"
                              >
                                Editor
                              </span>
                            </div>
                          </div>
                        </div>
                      </td>
                      <td class="whitespace-nowrap p-2 text-end text-sm font-medium">
                        <button
                          type="button"
                          @click="deleteManageCollaborator(users.collaboratorEmail as string)"
                        >
                          <FontAwesomeIcon
                            icon="fa-trash-can"
                            class="icon-ide-default hover:icon-ide-emphasized h-4 w-4 transition-colors"
                          />
                        </button>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- General Access -->
      <div class="surface-primary-header mt-3 flex flex-wrap items-center gap-y-5 rounded-lg p-3">
        <div class="flex-1">
          <h5 class="text-high mb-2 text-sm font-semibold">General access</h5>
          <div class="flex gap-x-3">
            <div class="background-tertiary flex h-max w-max rounded-full p-2">
              <FontAwesomeIcon
                v-if="generalAccess.isFetching"
                spin
                icon="fa-spinner"
                class="h-4 w-4"
              />
              <Globe v-else class="icon-ide-default" />
            </div>
            <div :class="generalAccess.isFetching && 'pointer-events-none opacity-25'">
              <div class="hs-dropdown relative inline-flex w-full [--offset:6]">
                <h6 class="text-high cursor-pointer space-x-2 text-sm font-medium">
                  <span v-if="generalAccess.type === 'ANYONE'">Anyone with the link</span>
                  <span v-else-if="generalAccess.type === 'ACCOUNT'">Anyone within the team</span>
                  <span v-else-if="generalAccess.type === 'RESTRICTED'">Restricted</span>
                  <FontAwesomeIcon icon="fa-angle-down" />
                </h6>

                <div
                  class="hs-dropdown-menu duration stroke-input-default surface-primary-base z-10 mt-2 hidden w-full max-w-60 rounded-lg border opacity-0 shadow transition-[opacity,margin] before:absolute before:-top-1 before:start-0 before:h-1 before:w-full after:absolute after:-bottom-4 after:start-0 after:h-4 after:w-full hs-dropdown-open:opacity-100"
                  role="menu"
                  aria-orientation="vertical"
                  aria-labelledby="hs-dropdown-default"
                >
                  <div class="space-y-0.5 p-1">
                    <span
                      class="hover:surface-primary-header flex cursor-pointer items-center gap-x-3.5 rounded-md px-3 py-2 text-sm focus:outline-none"
                      @click="handleUpdateType(AccessType.ANYONE)"
                    >
                      Anyone with the link
                      <FontAwesomeIcon
                        v-if="generalAccess.type === AccessType.ANYONE"
                        icon="fa-check"
                        class="ml-auto h-3 w-3"
                      />
                    </span>
                    <span
                      class="hover:surface-primary-header flex cursor-pointer items-center gap-x-3.5 rounded-md px-3 py-2 text-sm focus:outline-none"
                      @click="handleUpdateType(AccessType.ACCOUNT)"
                    >
                      Anyone within the team
                      <FontAwesomeIcon
                        v-if="generalAccess.type === AccessType.ACCOUNT"
                        icon="fa-check"
                        class="ml-auto h-3 w-3"
                      />
                    </span>
                    <span
                      class="hover:surface-primary-header flex cursor-pointer items-center gap-x-3.5 rounded-md px-3 py-2 text-sm focus:outline-none"
                      @click="handleUpdateType(AccessType.RESTRICTED)"
                    >
                      Restricted
                      <FontAwesomeIcon
                        v-if="generalAccess.type === AccessType.RESTRICTED"
                        icon="fa-check"
                        class="ml-auto h-3 w-3"
                      />
                    </span>
                  </div>
                </div>
              </div>
              <p class="text-low cursor-default text-xs leading-loose">
                <template v-if="generalAccess.type === 'ANYONE'">
                  Anyone with the link can
                  <span class="text-mid">
                    {{ generalAccess.permission === 'EDITOR' ? 'edit' : 'view' }}
                  </span>
                </template>
                <template v-else-if="generalAccess.type === 'ACCOUNT'">
                  Anyone in your team can
                  <span class="text-mid">
                    {{ generalAccess.permission === 'EDITOR' ? 'edit' : 'view' }}
                  </span>
                </template>
                <template v-else-if="generalAccess.type === 'RESTRICTED'">
                  Only people with access can
                  <span class="text-mid">
                    {{ generalAccess.permission === 'EDITOR' ? 'edit' : 'view' }}
                  </span>
                  with the link
                </template>
              </p>
            </div>
          </div>
        </div>
        <div v-if="generalAccess.type !== 'RESTRICTED'" class="text-right lg:flex-1 lg:text-left">
          <h5 class="text-high mb-2 text-sm font-semibold">Permission</h5>

          <div
            id="permission-dropdown"
            class="hs-dropdown relative inline-flex w-full max-w-24 [--offset:2] sm:max-w-40 lg:max-w-60"
          >
            <div
              ref="permissionTglRef"
              class="hs-dropdown-toggle color-secondary-600 stroke-input-default background-primary flex w-full cursor-pointer items-center justify-between gap-3 rounded-lg border px-3 py-2 sm:py-2.5"
            >
              <p class="text-mid text-sm">
                {{ generalAccess.permission === 'EDITOR' ? 'Editor' : 'Viewer' }}
              </p>
              <FontAwesomeIcon icon="fa-angle-down" class="h-3 w-3" />
            </div>

            <div
              ref="permissionDdlRef"
              class="hs-dropdown-menu duration stroke-input-default surface-primary-base z-10 mt-2 hidden w-full max-w-60 rounded-lg border opacity-0 shadow transition-[opacity,margin] before:absolute before:-top-1 before:start-0 before:h-1 before:w-full after:absolute after:-bottom-4 after:start-0 after:h-4 after:w-full hs-dropdown-open:opacity-100"
              role="menu"
              aria-orientation="vertical"
              aria-labelledby="hs-dropdown-default"
            >
              <div class="space-y-0.5 p-1">
                <span
                  class="hover:surface-primary-header flex cursor-pointer items-center gap-x-3.5 rounded-md px-3 py-2 text-sm focus:outline-none"
                  @click="handleUpdatePermission(AccessPermission.READ_ONLY)"
                >
                  Viewer
                  <FontAwesomeIcon
                    v-if="generalAccess.permission === AccessPermission.READ_ONLY"
                    icon="fa-check"
                    class="ml-auto h-3 w-3"
                  />
                </span>
                <span
                  class="hover:surface-primary-header flex cursor-pointer items-center gap-x-3.5 rounded-md px-3 py-2 text-sm focus:outline-none"
                  @click="handleUpdatePermission(AccessPermission.EDITOR)"
                >
                  Editor
                  <FontAwesomeIcon
                    v-if="generalAccess.permission === AccessPermission.EDITOR"
                    icon="fa-check"
                    class="ml-auto h-3 w-3"
                  />
                </span>
              </div>
            </div>
          </div>
        </div>
        <div
          v-if="generalAccess.type !== 'RESTRICTED'"
          class="my-auto flex-[100%] lg:mx-3 lg:flex-none"
        >
          <Button
            pill
            class="flex w-full min-w-40 items-center justify-center gap-x-1.5 px-3 lg:gap-x-3 lg:px-6"
            @click="handleCopyURL"
            :disabled="generalAccess.awaitingURL"
          >
            <FontAwesomeIcon icon="fa-link" class="h-5 w-5" />
            <span> {{ urlCopied ? 'Copied' : 'Copy' }} URL </span>
          </Button>
        </div>
      </div>
    </div>
  </div>
</template>
